import {
  Briefcase,
  Building2,
  CalendarClock,
  Clock,
  Command,
  FileSignature,
  FileText,
  KanbanSquare,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Package,
  Search,
  Settings,
  Sparkles,
  Star,
  Target,
  UserCog,
  Users,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth'
import { cn } from '../../lib/utils'
import { Link, useNavigate, useRoute } from '../../router'

type NavItem = {
  path: string
  label: string
  icon: typeof LayoutDashboard
  hint?: string
  /** Only visible to owner/admin (e.g. user management, tenant config). */
  adminOnly?: boolean
}

const isAdminRole = (role?: string) => role === 'owner' || role === 'admin'

type NavSection = {
  title: string
  items: NavItem[]
}

const navSections: NavSection[] = [
  {
    title: 'Mission',
    items: [{ path: '/', label: 'Dashboard', icon: LayoutDashboard, hint: 'Panoramica' }],
  },
  {
    title: 'Commerciale',
    items: [
      { path: '/clients', label: 'Clienti', icon: Users, hint: 'Anagrafica clienti' },
      { path: '/projects', label: 'Progetti', icon: Briefcase, hint: 'Progetti attivi e chiusi' },
      { path: '/leads', label: 'Leads', icon: Target, hint: 'Pipeline commerciale' },
      { path: '/proposals', label: 'Proposte', icon: FileSignature, hint: 'PRV + CTR' },
      {
        path: '/proposal-modules',
        label: 'Catalogo moduli',
        icon: Package,
        hint: 'Voci riusabili nei PRV',
      },
      { path: '/invoices', label: 'Fatture', icon: FileText, hint: 'Fatturato e scadenze' },
      {
        path: '/scadenziario',
        label: 'Scadenziario',
        icon: CalendarClock,
        hint: 'Manutenzioni e contratti ricorrenti clienti',
      },
    ],
  },
  {
    title: 'Esecuzione',
    items: [
      { path: '/agile', label: 'Agile', icon: KanbanSquare, hint: 'Feature, US e Task' },
      { path: '/time', label: 'Tempi', icon: Clock, hint: 'Time tracking e report' },
      { path: '/patterns', label: 'Patterns', icon: Sparkles, hint: 'Knowledge base tecnica' },
      { path: '/workflows', label: 'Workflow', icon: ListChecks, hint: 'SOP e procedure' },
    ],
  },
  {
    title: 'Sistema',
    items: [
      { path: '/team', label: 'Team', icon: UserCog, hint: 'Utenti e permessi', adminOnly: true },
      {
        path: '/settings/tenant',
        label: 'Tenant',
        icon: Building2,
        hint: 'Config azienda',
        adminOnly: true,
      },
      { path: '/setup', label: 'Setup', icon: Settings, hint: 'Utenti e seed', adminOnly: true },
    ],
  },
]

const flatNav = navSections.flatMap((s) => s.items)

function BrandMark({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'size-7' : 'size-9'
  const iconDim = size === 'sm' ? 'size-3.5' : 'size-[18px]'
  return (
    <div className="relative">
      <div aria-hidden className="absolute inset-0 rounded-md bg-cyan-500/40 blur-md -z-10" />
      <div
        className={cn(
          dim,
          'relative rounded-md grid place-items-center',
          'bg-gradient-to-br from-cyan-400 via-cyan-500 to-violet-600',
          'shadow-[0_0_0_1px_rgba(34,211,238,0.5)_inset,0_2px_12px_-2px_rgba(34,211,238,0.6)]',
          'overflow-hidden',
        )}
      >
        <span
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              'radial-gradient(1px 1px at 25% 30%, rgba(255,255,255,0.9), transparent 60%), radial-gradient(1px 1px at 70% 65%, rgba(255,255,255,0.7), transparent 60%), radial-gradient(1px 1px at 50% 80%, rgba(255,255,255,0.8), transparent 60%)',
          }}
        />
        <Star
          className={cn(
            iconDim,
            'relative text-zinc-50 fill-zinc-50/90 drop-shadow-[0_0_2px_rgba(255,255,255,0.7)]',
          )}
          strokeWidth={1.4}
        />
      </div>
    </div>
  )
}

function isPlatformMac() {
  if (typeof navigator === 'undefined') return false
  // userAgentData is non-standard but useful when available
  // biome-ignore lint/suspicious/noExplicitAny: optional API
  const platform = (navigator as any).userAgentData?.platform ?? navigator.platform ?? ''
  return /mac/i.test(platform)
}

function CommandPalette({
  open,
  onClose,
  go,
}: {
  open: boolean
  onClose: () => void
  go: (path: string) => void
}) {
  const { user } = useAuth()
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const nav = useMemo(
    () => flatNav.filter((n) => !n.adminOnly || isAdminRole(user?.role)),
    [user?.role],
  )

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return nav
    return nav.filter(
      (n) =>
        n.label.toLowerCase().includes(q) ||
        n.path.toLowerCase().includes(q) ||
        (n.hint ?? '').toLowerCase().includes(q),
    )
  }, [query, nav])

  useEffect(() => {
    if (open) {
      setQuery('')
      setSelected(0)
      // focus next tick
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — reset highlight to top whenever query changes
  useEffect(() => {
    setSelected(0)
  }, [query])

  if (!open) return null

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelected((s) => Math.min(s + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelected((s) => Math.max(s - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = results[selected]
      if (target) {
        go(target.path)
        onClose()
      }
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh] animate-page-in">
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-zinc-950/85 backdrop-blur-md"
        aria-label="Chiudi"
      />
      <div
        className={cn(
          'relative w-full max-w-xl rounded-xl border border-zinc-800 bg-zinc-900/95 overflow-hidden animate-scale-in',
          'shadow-[0_24px_80px_-12px_rgba(0,0,0,0.7),0_0_0_1px_rgba(34,211,238,0.15)_inset,0_0_80px_-12px_rgba(34,211,238,0.35)]',
        )}
      >
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/60 to-transparent"
        />
        <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800/80">
          <Search className="size-4 text-cyan-300/80 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Vai a..."
            className="flex-1 bg-transparent text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
          />
          <kbd className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-800 rounded px-1.5 py-0.5">
            esc
          </kbd>
        </div>
        <ul className="max-h-[50vh] overflow-y-auto py-1.5">
          {results.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-zinc-500">Nessun risultato</li>
          )}
          {results.map((item, idx) => {
            const Icon = item.icon
            const active = idx === selected
            return (
              <li key={item.path}>
                <button
                  type="button"
                  onMouseEnter={() => setSelected(idx)}
                  onClick={() => {
                    go(item.path)
                    onClose()
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2 text-[13px] text-left transition-colors',
                    active
                      ? 'bg-gradient-to-r from-cyan-500/15 via-cyan-500/8 to-violet-500/10 text-zinc-50'
                      : 'text-zinc-300 hover:bg-zinc-800/60',
                  )}
                >
                  <Icon
                    className={cn('size-4 shrink-0', active ? 'text-cyan-300' : 'text-zinc-500')}
                  />
                  <span className="font-medium">{item.label}</span>
                  {item.hint && (
                    <span className="text-[11px] text-zinc-500 truncate">— {item.hint}</span>
                  )}
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-600">
                    {item.path}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
        <div className="border-t border-zinc-800/80 px-4 py-2 flex items-center gap-4 text-[10.5px] text-zinc-500">
          <span className="flex items-center gap-1.5">
            <kbd className="border border-zinc-800 rounded px-1 py-0.5">↑</kbd>
            <kbd className="border border-zinc-800 rounded px-1 py-0.5">↓</kbd>
            naviga
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="border border-zinc-800 rounded px-1 py-0.5">enter</kbd>
            apri
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-cyan-400 pulse-cyan" />
            Squadron
          </span>
        </div>
      </div>
    </div>
  )
}

function NavLinkRow({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon
  return (
    <Link
      to={item.path}
      className={cn(
        'group relative flex items-center gap-3 rounded-md px-3 py-2 text-[13px] transition-all duration-150',
        active
          ? 'bg-gradient-to-r from-cyan-500/15 via-cyan-500/8 to-violet-500/10 text-zinc-50 shadow-[0_0_0_1px_rgba(34,211,238,0.2)_inset,0_0_18px_-6px_rgba(34,211,238,0.5)]'
          : 'text-zinc-400 hover:bg-zinc-800/40 hover:text-zinc-100',
      )}
    >
      {active && (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-gradient-to-b from-cyan-400 to-violet-500 shadow-[0_0_6px_0_rgba(34,211,238,0.7)]" />
      )}
      <Icon
        className={cn(
          'size-4 shrink-0 transition-colors',
          active
            ? 'text-cyan-300 drop-shadow-[0_0_4px_rgba(34,211,238,0.5)]'
            : 'text-zinc-500 group-hover:text-zinc-300',
        )}
      />
      <span className="font-medium">{item.label}</span>
    </Link>
  )
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const route = useRoute()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const isMac = isPlatformMac()

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = isMac ? e.metaKey : e.ctrlKey
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((s) => !s)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isMac])

  const navigate = useNavigate()

  function isActive(path: string) {
    if (path === '/') return route.path === '/'
    return route.path === path || route.path.startsWith(`${path}/`)
  }

  function go(path: string) {
    navigate(path)
  }

  const isAdmin = isAdminRole(user?.role)
  const visibleSections = navSections
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.adminOnly || isAdmin) }))
    .filter((s) => s.items.length > 0)

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-60 shrink-0 border-r border-zinc-800/70 bg-zinc-950/60 backdrop-blur-md flex-col relative">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-cyan-500/20 to-transparent"
        />

        {/* Brand */}
        <div className="px-4 py-5 border-b border-zinc-800/70 flex items-center gap-3">
          <BrandMark />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="font-display font-semibold tracking-tight text-zinc-50 leading-none">
                Squadron
              </div>
              <span className="size-1.5 rounded-full bg-cyan-400 pulse-cyan" />
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-zinc-500 mt-1.5">
              g-solutions
            </div>
          </div>
        </div>

        {/* Command launcher */}
        <div className="px-2.5 pt-3">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className={cn(
              'w-full flex items-center gap-2 rounded-md px-3 py-1.5 text-[12px] text-zinc-400',
              'border border-zinc-800/70 bg-zinc-900/40 hover:border-cyan-500/30 hover:bg-zinc-900/60 hover:text-zinc-200',
              'transition-colors',
            )}
          >
            <Search className="size-3.5 text-zinc-500" />
            <span className="flex-1 text-left">Cerca pagina</span>
            <kbd className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-800 rounded px-1 py-0.5">
              {isMac ? '⌘' : 'Ctrl'} K
            </kbd>
          </button>
        </div>

        {/* Nav sections */}
        <nav className="flex-1 px-2.5 py-3 overflow-y-auto">
          {visibleSections.map((section, idx) => (
            <div key={section.title} className={idx > 0 ? 'mt-4' : ''}>
              <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.14em] text-zinc-600 font-semibold">
                {section.title}
              </div>
              <div className="space-y-px">
                {section.items.map((item) => (
                  <NavLinkRow key={item.path} item={item} active={isActive(item.path)} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User block */}
        <div className="border-t border-zinc-800/70 px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2.5">
            <div className="relative shrink-0">
              <div
                aria-hidden
                className="absolute inset-0 rounded-full bg-cyan-500/30 blur-sm -z-10"
              />
              <div className="size-7 rounded-full bg-gradient-to-br from-violet-500 via-cyan-500 to-cyan-400 grid place-items-center text-zinc-950 text-[11px] font-bold shrink-0 shadow-[0_0_0_1px_rgba(34,211,238,0.4)_inset]">
                {user?.name
                  ?.split(' ')
                  .map((s) => s[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase() ?? '?'}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[13px] text-zinc-100 truncate font-medium">{user?.name}</div>
              <div className="text-[10.5px] text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800/60 rounded-md p-1.5 transition-colors shrink-0"
            aria-label="Esci"
            title="Esci"
          >
            <LogOut className="size-4" />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden border-b border-zinc-800/70 px-4 py-3 flex items-center justify-between bg-zinc-950/80 backdrop-blur-md">
          <div className="flex items-center gap-2.5">
            <BrandMark size="sm" />
            <span className="font-display font-semibold text-zinc-50">Squadron</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="text-zinc-400 hover:text-zinc-100 p-1.5 rounded-md hover:bg-zinc-800/60"
              aria-label="Cerca"
              title="Cerca"
            >
              <Command className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => void logout()}
              className="text-zinc-500 hover:text-zinc-100 p-1.5 rounded-md hover:bg-zinc-800/60"
              aria-label="Esci"
            >
              <LogOut className="size-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-8 sm:px-10 sm:py-10 max-w-7xl mx-auto w-full animate-page-in">
          {children}
        </div>
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} go={go} />
    </div>
  )
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <header className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-zinc-800/50 pb-5 relative">
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-x-4 -top-2 bottom-0 -z-10 opacity-70"
        style={{
          background:
            'radial-gradient(60% 80% at 0% 50%, rgba(34,211,238,0.06), transparent 70%), radial-gradient(50% 80% at 100% 50%, rgba(167,139,250,0.05), transparent 70%)',
        }}
      />
      <div>
        <h1 className="font-display text-2xl sm:text-[28px] font-semibold tracking-[-0.02em] bg-gradient-to-r from-zinc-50 via-zinc-100 to-zinc-400 bg-clip-text text-transparent">
          {title}
        </h1>
        {description && (
          <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">{description}</p>
        )}
      </div>
      {action}
    </header>
  )
}

export function SectionTitle({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
      <div>
        <h2 className="font-display text-base font-semibold tracking-tight text-zinc-100">
          {title}
        </h2>
        {description && <p className="text-xs text-zinc-500 mt-0.5">{description}</p>}
      </div>
      {action}
    </div>
  )
}
